import { useEffect, useState } from "react";

import { cn } from "@verse_core/shared_components/ClassName";
import { useRive } from "@rive-app/react-canvas";

interface RiveAnimationProps {
  src?: string;
  className?: string;
  fallbackClassName?: string;
  fallbackSrc?: string;
}

/**
 * RiveAnimation component
 * @param src - The source of the Rive animation
 * @param className - The class name of the Rive animation
 * @param fallbackClassName - The class name of the fallback image
 * @param fallbackSrc - fallback image if the Rive animation is not supported
 * @returns The Rive animation component
 */
export const RiveAnimation = ({
  src,
  className,
  fallbackClassName,
  fallbackSrc,
}: RiveAnimationProps) => {
  const [isRiveSupported, setIsRiveSupported] = useState(true);

  useEffect(() => {
    const checkRiveSupport = async () => {
      try {
        await import("@rive-app/canvas");
        setIsRiveSupported(true);
      } catch (error) {
        console.log("Rive not supported:", error);
        setIsRiveSupported(false);
      }
    };

    checkRiveSupport();
  }, []);

  const { RiveComponent } = useRive({
    src,
    autoplay: true,
    useOffscreenRenderer: true,
  });

  if (!isRiveSupported || !src) {
    return (
      <img
        src={fallbackSrc}
        className={cn("tw-object-contain", fallbackClassName)}
        alt="fallback"
      />
    );
  }

  return <RiveComponent className={className} />;
};
